import { yupResolver } from '@hookform/resolvers/yup';
import {
  AbsenceType,
  AppointmentStatusCodes,
  ContactType,
  CreateCaseDto,
  QueryFactory,
  ResourceType,
  VisitDto,
  WaitingListPriority,
} from '@lmt/shared-types';

import { useOpenCalendarTab } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/useOpenCalendarTab';
import {
  AlertPatientOption,
  patientResourceType,
  ResourceGroups,
  ResourceToPick,
  SelectedResources,
  TakenTimeSlotsForResources,
  VisitCreatingFormData,
  VisitCreatingOrEditingFormProps,
  VisitType,
} from '@lmt/unicorn-shared/app/components/calendar/visit/common/types';
import { VisitCreatingExpandedForm } from '@lmt/unicorn-shared/app/components/calendar/visit/components/VisitCreatingExpandedForm';
import { VisitCreatingMiniForm } from '@lmt/unicorn-shared/app/components/calendar/visit/components/VisitCreatingMiniForm';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { stringifyDateTimeIntervalForMemoization } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DateTimeIntervalPicker';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import { useActiveAttendingDoctors } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { SettingsActions } from '@lmt/unicorn-shared/app/features/settings/reducer';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useCases } from '@lmt/unicorn-shared/app/helpers/case-queries';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { getDayTimeInMillis } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { getFirstDayOfWeek } from '@lmt/unicorn-shared/app/helpers/date-helper-devexpress';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import {
  convertToErrorString,
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { localizeCaseTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import PatientIcon from '@lmt/unicorn-shared/app/icons/patient.svg?react';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  addMilliseconds,
  addSeconds,
  addWeeks,
  Interval,
  startOfDay,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { SearchInputPatientData } from '../../common/patient/PatientSearch';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import { SubmitButton } from '../../common/ui-kit/buttons/SubmitButton';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { getFullName } from '../../patients/patient-form/common/common';
import { eventTypeTranslationKeys } from '../EventTypeSelection';
import {
  getCreateFormDefaultValues,
  resourceSettings,
} from './common/settings';
import {
  convertIntervalToFormField,
  durationInMills,
  isSelectedIntervalAlreadyTaken,
  mergeTakenTimeSlots,
} from './common/time-operations';
import { visitYupSchema } from './common/validation';

import styles from './VisitCreatingOrEditingForm.module.scss';
import { useEventCallback } from '@mui/material';

const firstDayOfWeekIndex = getFirstDayOfWeek();

const unknownVisitTypeValue = 'visit_type.unknown';

export const VisitCreatingOrEditingForm: FC<VisitCreatingOrEditingFormProps> = (
  props,
) => {
  const {
    patientId,
    formId,
    formType,
    tryCompleteForm,
    isExpanded,
    expand,
    eventTypeSelection,
    visitId,
    caseId: defaultCaseId,
    assignedResources,
    loadBlocking,
    registerFormDirtyStateRetriever,
    isWeekView,
    calendarType,
    defaultVisitStartTimeInSeconds,
  } = props;

  const i18n = useTranslation();
  const globalForms = useGlobalForms();
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const isOpenVisit = useUniSelector(getSettings).isOpenVisitAfterCreation;
  const queryClient = useQueryClient();
  const nameof = nameofFactory<VisitCreatingFormData>();
  const claims = useClaims();

  const [status, setStatus] = useState<boolean>(false);

  const [visitToEdit, setVisitToEdit] = useState<VisitDto | undefined>(
    undefined,
  );

  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
    patientId!,
    {
      enabled: !!patientId,
    },
  );

  const defaultFormValues = useMemo(() => {
    return getCreateFormDefaultValues({
      ...props,
      patient: patientQuery?.data,
    });
  }, [patientQuery?.data]);
  const createForm = () =>
    useForm<VisitCreatingFormData>({
      resolver: yupResolver(visitYupSchema) as any,
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      shouldUnregister: false,
      defaultValues: defaultFormValues,
    });

  const miniForm = createForm();
  const expandedForm = createForm();
  const form = isExpanded ? expandedForm : miniForm;
  const { handleSubmit, watch, setError, formState, register } = form;

  const [
    patient,
    caseId,
    dateTimeInterval,
    visitTypeId,
    inWaitingList,
    priority,
  ] = watch([
    'patient',
    'caseId',
    'dateTimeInterval',
    'visitTypeId',
    'waitingList',
    'priority',
  ]);

  const visitTypes = useActiveVisitTypes(visitTypeId);

  const unknownVisitType = useMemo(
    () => visitTypes.find((type) => type.value === unknownVisitTypeValue),
    [visitTypes],
  );

  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      // if we edit visit and visit has type 'Unknown' we shouldn't exclude this type from list
      (!!visitToEdit &&
      !!unknownVisitType &&
      visitToEdit.typeId === unknownVisitType.id
        ? visitTypes
        : // otherwise we should filter types to prevent creating of new visit with type 'Unknown'
          visitTypes.filter((type) => type.value !== unknownVisitTypeValue)
      ).map((type) => ({
        id: type.id,
        label: i18n.t(type.value as any),
        specificStartTimeInSecondsSinceMidnight:
          type.specificStartTimeInSecondsSinceMidnight,
        defaultDurationInSeconds: type.defaultDurationInSeconds,
      })),
    [visitTypes, unknownVisitType, visitToEdit],
  );

  const expandedFormDirtyFields = expandedForm.formState.dirtyFields;
  const miniFormDirtyFields = miniForm.formState.dirtyFields;

  useEffect(() => {
    registerFormDirtyStateRetriever(
      formId,
      () =>
        Object.keys(miniFormDirtyFields).length > 0 ||
        Object.keys(expandedFormDirtyFields).length > 0,
    );
  }, [expandedFormDirtyFields, miniFormDirtyFields]);

  /**
   * Resource types with enabled "Any available"
   * We store them in user settings (local storage) for new visits
   * But we shouldn't select any available resources automatically if EXISTING visit is being edited
   * (only if user enables 'Any available' consciously)
   */
  const [typesWithAnyAvailableResource, setTypesWithAnyAvailableResource] =
    useState(
      visitId
        ? []
        : (useUniSelector(getSettings).resourceTypesWithEnabledAnyAvailable ??
            []),
    );

  const changeTypesWithAnyAvailableResource = useCallback(
    (types: string[]) => {
      setTypesWithAnyAvailableResource(types);
      if (!visitId) {
        // if it's a new visit form we should also update types with enabled 'Any available' in user settings
        dispatch(
          SettingsActions.resourceTypesWithEnabledAnyAvailableChanged(types),
        );
      }
    },
    [visitId, dispatch],
  );

  useEffect(
    function copyValuesFromOneFormToTheOtherOnExpandCollapse() {
      if (isExpanded) {
        expandedForm.reset({
          ...expandedForm.getValues(),
          ...miniForm.getValues(),
        });
      } else {
        miniForm.reset({
          ...miniForm.getValues(),
          ...expandedForm.getValues(),
        });
      }
    },
    [isExpanded],
  );

  const setValue = useCallback<(typeof miniForm)['setValue']>(
    (name, value, options) => {
      miniForm.setValue(name, value as any, options);
      expandedForm.setValue(name, value as any, options);
    },
    [],
  );

  const onIntervalChanged = useCallback(
    (newInterval: Interval) => {
      setValue('dateTimeInterval', convertIntervalToFormField(newInterval));
    },
    [setValue],
  );

  const selectedVisitType = useMemo(() => {
    return visitTypes.find((type) => type.id === visitTypeId);
  }, [visitTypeId]);

  const startDateTime = useMemo(
    () =>
      dateTimeInterval.startDate &&
      dateTimeInterval.startTimeInMills !== undefined &&
      dateTimeInterval.startTimeInMills !== null
        ? addMilliseconds(
            dateTimeInterval.startDate,
            dateTimeInterval.startTimeInMills,
          )
        : undefined,
    [stringifyDateTimeIntervalForMemoization(dateTimeInterval)],
  );

  useEffect(
    function setDefaultVisitTypeInterval() {
      if (
        !selectedVisitType ||
        !startDateTime ||
        (!form.formState.dirtyFields.visitTypeId && visitId)
      ) {
        return;
      }
      // we need to use default interval of visit type if visit is being created from timeline week or month view
      // (or in mini-form where 'calendarType' is undefined)
      // since in these views cells take the whole day (i.e. there is no specific time)
      const shouldUseVisitTypeDefaultStartTime =
        !calendarType ||
        calendarType === 'timelineWeek' ||
        calendarType === 'month';

      let visitStart: Date = startDateTime;
      if (shouldUseVisitTypeDefaultStartTime) {
        const startTime =
          selectedVisitType.specificStartTimeInSecondsSinceMidnight ??
          defaultVisitStartTimeInSeconds;
        if (startTime) {
          // set default start time of visit type
          visitStart = addSeconds(startOfDay(visitStart), startTime);
        }
      }
      const visitEnd = addSeconds(
        visitStart,
        selectedVisitType.defaultDurationInSeconds,
      );
      onIntervalChanged({
        start: visitStart,
        end: visitEnd,
      });
    },
    [selectedVisitType],
  );

  const [doctorsSearchQuery, setDoctorsSearchQuery] = useState<string | null>(
    null,
  );
  const [doctorsSearchQueryDebounced] = useDebounce(doctorsSearchQuery, 100);

  const currentDoctorId = form.watch(nameof('attendingDoctorId'));
  const attendingDoctors = useActiveAttendingDoctors(
    doctorsSearchQueryDebounced,
    currentDoctorId,
  );

  const casesQuery = useCases(patient?.id);
  const cases = casesQuery.data ?? [];

  const addNewCase = useCallback(
    async (patientId?: number) => {
      if (!patientId) {
        return;
      }
      if (!!visitId) {
        const isConfirmed = await modals.showConfirm({
          title: i18n.t('event.visit.confirm_move_visit_title'),
          text: i18n.t('event.visit.confirm_move_visit_text'),
          okButtonText: i18n.t('event.visit.confirm_move_visit_button'),
          okButtonColor: ButtonColor.SuperDanger,
        });
        if (!isConfirmed) {
          return;
        }
      }
      const startDate =
        form.getValues('dateTimeInterval.startDate') ?? startOfToday();
      const newCase: CreateCaseDto = {
        patientId: patientId,
        startDate: startDate,
        endDate: null,
      };
      const createdCase = await QueryFactory.CaseClient.createCase(newCase);
      if (createdCase) {
        queryClient.setQueryData(
          QueryFactory.CaseQuery.getCasesQueryKey(patientId),
          {
            data: cases.concat({
              ...createdCase,
              title: localizeCaseTitle(createdCase),
              isClosed: false,
            }),
          },
        );

        setValue('caseId', createdCase.id, validateAndDirty);
      }
    },
    [cases],
  );

  const subtenantsFilter: Set<number> = useMemo(
    () => (patient ? new Set([patient.subtenantId]) : new Set()),
    [patient?.subtenantId],
  );

  const [resourcesLoaded, setResourcesLoaded] = useState<boolean>(false);
  const [resourceGroups, setResourceGroups] = useState<ResourceGroups>({
    [patientResourceType]: [],
  });

  // if default case is selected, it should be done only once for patient
  // otherwise it causes bug #41711: default case is selected every time when we add new case
  const isDefaultCaseAlreadySet = useRef(false);

  useEffect(
    function setDefaultCase() {
      if (isDefaultCaseAlreadySet.current) {
        return;
      }
      if (!visitId && !defaultCaseId) {
        const filteredCases = cases.filter((pCase) => !pCase.isClosed);
        if (filteredCases.length > 0) {
          const latestCase = filteredCases.reduce((a, b) =>
            a.startDate > b.startDate ? a : b,
          );
          setValue('caseId', latestCase?.id ?? null, {
            shouldValidate: true,
          });
          isDefaultCaseAlreadySet.current = true;
        }
      }
    },
    [cases],
  );

  useEffect(
    function resetFlagOnPatientChange() {
      isDefaultCaseAlreadySet.current = false;
    },
    [patient],
  );

  useEffect(() => {
    const result = patientQuery.data;
    if (result) {
      form.setValue(nameof('patient'), result);
      form.setValue(nameof('priorityContactType'), result.priorityContactType);
    }
  }, [patientQuery.data]);

  useEffect(function loadVisitToEdit() {
    if (!visitId) {
      return;
    }
    const loadVisit = async () => {
      const visit =
        await QueryFactory.AppointmentEventsClient.getVisit(visitId);
      setVisitToEdit(visit);
      const patient = await QueryFactory.PatientClient.getPatient(
        visit.patientId!,
      );
      if (patient) {
        form.setValue(nameof('patient'), patient);
        form.setValue(
          nameof('priorityContactType'),
          patient.priorityContactType,
        );
      }
      form.setValue(nameof('caseId'), visit.caseId);
      form.setValue(nameof('dateTimeInterval'), {
        startDate: startOfDay(visit.start),
        startTimeInMills: getDayTimeInMillis(visit.start),
        endDate: startOfDay(visit.end),
        endTimeInMills: getDayTimeInMillis(visit.end),
      });
      form.setValue(nameof('visitTypeId'), visit.typeId);
      form.setValue(nameof('note'), visit.note);
      form.setValue(nameof('priority'), visit.priority);
      form.setValue(nameof('waitingList'), visit.inWaitingList);
      form.setValue(
        nameof('attendingDoctorId'),
        visit.attendingDoctorId ?? null,
      );
      form.setValue(nameof('statusId'), visit.statusId ?? null);
      form.setValue(nameof('number'), visit.number);
      form.setValue(nameof('numberSource'), visit.numberSource);
      form.setValue(nameof('id'), visitId);
    };
    loadVisit().catch((e) => console.error('loadVisitToEdit', e));
  }, []);

  useEffect(
    function preselectResourcesIfVisitIsEdited() {
      if (visitToEdit && resourcesLoaded) {
        const preselectedResources: SelectedResources = {};

        if (visitToEdit.patientId) {
          const patientAsResource = resourceGroups[patientResourceType].find(
            (patient) => patient.id === visitToEdit.patientId,
          );
          if (patientAsResource) {
            preselectedResources[patientResourceType] = [patientAsResource];
          }
        }

        // set resources of other types for visit being edited
        if (visitToEdit.resourceIds?.length) {
          Object.keys(resourceGroups)
            .filter((key) => key !== patientResourceType)
            .map((key) => {
              preselectedResources[key] = resourceGroups[key].filter((item) =>
                visitToEdit.resourceIds?.includes(item.id),
              );
            });
        }

        if (!isEmpty(preselectedResources)) {
          setSelectedResources(preselectedResources);
          setValue('resources', preselectedResources, { shouldDirty: false });
        }
        excludeDeletedResources();
      }
    },
    [resourcesLoaded, visitToEdit],
  );

  const excludeDeletedResources = useCallback(() => {
    Object.keys(resourceGroups)
      .filter((key) => key !== patientResourceType)
      .map((key) => {
        resourceGroups[key] = resourceGroups[key].filter(
          (item) => !item.isDeleted,
        );
      });
  }, [resourceGroups]);

  useEffect(
    function preselectResourcesIfResourcesArePassed() {
      if (!resourcesLoaded) {
        return;
      }
      const preselectedResources: SelectedResources = {};
      if (assignedResources?.length) {
        Object.keys(resourceGroups)
          .filter((key) => key != patientResourceType)
          .forEach((key) => {
            const foundResources = resourceGroups[key].filter((item) =>
              assignedResources.includes(item.id),
            );
            if (foundResources?.length) {
              preselectedResources[key] = foundResources;
              // if preselected resource(s) was passed, 'Any available' should be disabled for current resource type
              changeTypesWithAnyAvailableResource(
                typesWithAnyAvailableResource.filter((type) => type !== key),
              );
            }
          });
      }
      if (!isEmpty(preselectedResources)) {
        setSelectedResources((resources) => {
          setValue(
            'resources',
            { ...resources, ...preselectedResources },
            { shouldDirty: false },
          );
          return { ...resources, ...preselectedResources };
        });
      }
    },
    [resourcesLoaded, assignedResources],
  );

  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: !!visitId, // if we edit visit, we show all visit resources including deleted
  });
  const resources = resourcesQuery.data?.data ?? emptyArray();
  const isEditForm = !!visitId;

  const subtenantsDict = useSubtenantsDict();

  const patientAsResource = useMemo<ResourceToPick | null>(
    () =>
      patient
        ? {
            id: patient!.id,
            name: getFullName(patient),
            renderPrefix: () => <PatientIcon className={styles.patientIcon} />,
            renderPostfix: () => (
              <span className={styles.subtenantCode}>
                {subtenantsDict[patient.subtenantId]?.code}
              </span>
            ),
            renderText: () => {
              const patientName = getFullName(patient);
              return (
                <div
                  title={patientName}
                  className={clsx(styles.resourceName, styles.patientResource)}
                  onClick={() => {
                    // if (formId) {
                    //   closeForm(formId);
                    // }
                    tabs.openTab(
                      pages.patientProfile.link({
                        patientId: patient!.id,
                      }),
                    );
                  }}
                >
                  {patientName}
                </div>
              );
            },
            getTimeSlotsFromAppointments: (allAppointments) =>
              allAppointments.filter(
                (appointment) =>
                  ((!visitId || appointment.id !== visitId) &&
                    appointment.patientId === patient!.id) ||
                  appointment.absenceType === AbsenceType.Holiday,
              ),
          }
        : null,
    [patient?.id],
  );
  useEffect(() => {
    const loadAllResources = async () => {
      if (isEditForm && !resourcesLoaded && !patient) {
        return;
      }

      const resourceGroups = loadResourceGroups(resources, visitId);
      setResourceGroups({
        [patientResourceType]: patientAsResource ? [patientAsResource] : [],
        ...resourceGroups,
      });
      setResourcesLoaded(true);

      if (patient && patientAsResource) {
        setResourceSelected(
          patientResourceType,
          patient.id,
          true,
          patientAsResource,
          false,
        );
      } else {
        setResourceSelected(patientResourceType, null, false);
      }
    };
    loadAllResources().catch((e) => console.error('loadAllResources', e));
  }, [subtenantsFilter, patientAsResource, resources, visitId]);

  const [selectedResources, setSelectedResources] = useState<SelectedResources>(
    {},
  );

  const setResourceSelected = useCallback(
    (
      type: string,
      resourceId: number | null,
      selected: boolean,
      resource?: ResourceToPick,
      shouldDirty = true,
    ) => {
      setSelectedResources((actualSelectedResources) => {
        const newSelectedResources = { ...actualSelectedResources };
        if (selected) {
          const foundResource =
            resource ||
            resourceGroups[type]?.find(
              (resource) => resource.id === resourceId,
            );
          if (foundResource) {
            if (resourceSettings[type]?.isMultiSelect) {
              const selectedResourcesOfType = newSelectedResources[type] || [];
              newSelectedResources[type] = [
                ...selectedResourcesOfType,
                foundResource,
              ];
            } else {
              newSelectedResources[type] = [foundResource];
            }
          }
        } else if (
          !resourceId ||
          newSelectedResources[type]?.find(
            (resource) => resource.id === resourceId,
          )
        ) {
          if (!resourceId) {
            delete newSelectedResources[type];
          } else {
            newSelectedResources[type] = newSelectedResources[type].filter(
              (resource) => resource.id !== resourceId,
            );
          }
        }
        setValue('resources', newSelectedResources, {
          shouldDirty: shouldDirty,
        });
        return newSelectedResources;
      });
    },
    [resourceGroups],
  );

  const startOfShownDatePeriod = useMemo(() => {
    if (visitId) {
      return dateTimeInterval.startDate ?? -1; // -1 if visit has not been loaded yet
    }
    return dateTimeInterval.startDate ?? new Date();
  }, [dateTimeInterval]);
  const startOfWeekDate = startOfWeek(startOfShownDatePeriod, {
    weekStartsOn: firstDayOfWeekIndex,
  });
  const endOfWeekDate = addWeeks(startOfWeekDate, 1);
  const appointmentsQuery =
    QueryFactory.AppointmentEventsQuery.useSearchAppointmentsWithResourceOccupationQuery(
      {
        from: startOfWeekDate,
        to: endOfWeekDate,
        isWithoutCanceledVisits: true,
      },
      {
        enabled: startOfShownDatePeriod !== -1,
      },
    );
  const appointmentsList = useMemo(() => {
    const result = appointmentsQuery.data || [];
    if (!result.length) {
      return result;
    }
    const resourceIds = resources.map((x) => x.id);
    result.forEach((appointment) => {
      if (appointment.absenceType === AbsenceType.Holiday) {
        // we assign all resources to show them occupied if appointment is holiday
        appointment.resourceIds = resourceIds;
      }
    });
    return result;
  }, [appointmentsQuery.data, resources]);

  const takenTimeSlotsForResources: TakenTimeSlotsForResources = useMemo(() => {
    if (
      resourcesLoaded &&
      Object.keys(resourceGroups).length > 0 &&
      appointmentsList.length > 0
    ) {
      const takenTimeSlots: TakenTimeSlotsForResources = {};
      Object.keys(resourceGroups).forEach((type: string) => {
        takenTimeSlots[type] = resourceGroups[type].map((resource) => ({
          resourceId: resource.id,
          slots: mergeTakenTimeSlots(
            resource.getTimeSlotsFromAppointments(appointmentsList),
          ),
        }));
      });
      return takenTimeSlots;
    }
    return {};
  }, [resourceGroups, appointmentsList, resourcesLoaded]);

  const takenTimeSlots = useMemo(() => {
    const slotsForAllResources: Interval[] = [];
    Object.keys(selectedResources).forEach((type) => {
      const selectedResourcesForType = selectedResources[type];
      selectedResourcesForType.forEach((selectedResource) => {
        const takenTimeSlotsForSelectedResource = takenTimeSlotsForResources[
          type
        ]?.find(
          (resource) => resource.resourceId === selectedResource.id,
        )?.slots;
        if (takenTimeSlotsForSelectedResource) {
          slotsForAllResources.push(...takenTimeSlotsForSelectedResource);
        }
      });
    });
    return mergeTakenTimeSlots(slotsForAllResources);
  }, [selectedResources, takenTimeSlotsForResources]);

  const openVisit = useCallback(
    (visit: VisitDto, patient: SearchInputPatientData) => {
      const search = queryString.stringify({ block: `visit_${visit.id}` });

      tabs.openTab(
        pages.patientCases.link({
          patientId: patient.id,
          caseId: visit.caseId,
        }),
        search,
      );
    },
    [],
  );

  const alertPatientOptions: Array<AlertPatientOption> = useMemo(
    () => [
      {
        label: i18n.t('event.visit.alert_patient_options.day', { count: 1 }),
        intervalBeforeInMills: durationInMills({ days: 1 }),
      },
      {
        label: i18n.t('event.visit.alert_patient_options.day', { count: 2 }),
        intervalBeforeInMills: durationInMills({ days: 2 }),
      },
      {
        label: i18n.t('event.visit.alert_patient_options.week', { count: 1 }),
        intervalBeforeInMills: durationInMills({ weeks: 1 }),
      },
      {
        label: i18n.t('event.visit.alert_patient_options.week', { count: 2 }),
        intervalBeforeInMills: durationInMills({ weeks: 2 }),
      },
    ],
    [],
  );

  const ChangeOpenVisitSettings = useCallback(
    (isOpen: boolean) =>
      dispatch(SettingsActions.isOpenVisitAfterCreationChanged(isOpen)),
    [],
  );

  const showConflictDialog = useCallback(
    () =>
      modals.showConfirm({
        title: i18n.t('event.visit.already_taken_title'),
        text: i18n.t('event.visit.already_taken_text'),
        okButtonText: i18n.t('event.visit.save_anyway'),
      }),
    [dispatch],
  );

  const createOrUpdateVisit = useCallback(async (data: any) => {
    if (visitId) {
      return await QueryFactory.AppointmentEventsClient.updateVisit(
        visitId,
        data,
      );
    } else {
      return await QueryFactory.AppointmentEventsClient.createVisit(data);
    }
  }, []);

  const updatePatientPriorityContact = useCallback(
    async (patientId: number, contactType: ContactType) => {
      await QueryFactory.PatientClient.editPatient(patientId, {
        priorityContactType: contactType,
      });
    },
    [],
  );

  const submitForm = useErrorHandler<VisitCreatingFormData>(
    useEventCallback(async (data) => {
      const start = addMilliseconds(
        data.dateTimeInterval!.startDate!,
        data.dateTimeInterval!.startTimeInMills!,
      );
      const end = addMilliseconds(
        data.dateTimeInterval!.endDate!,
        data.dateTimeInterval!.endTimeInMills!,
      );
      const selectedInterval: Interval = {
        start,
        end,
      };

      let acceptConflict = false;
      if (
        isSelectedIntervalAlreadyTaken(selectedInterval, takenTimeSlots ?? [])
      ) {
        acceptConflict = (await showConflictDialog()) ?? false;
        if (!acceptConflict) {
          return;
        }
      }

      try {
        await tryCompleteForm(formType, formId, async () => {
          const caseId: number = data.caseId!;
          const selectedCase = cases.find(
            (caseOption) => caseOption.id === caseId,
          );
          if (!selectedCase) {
            return;
          }
          const resourceIds = Object.keys(data.resources)
            .filter((type) => type !== patientResourceType)
            .flatMap((type) => data.resources[type])
            .map((resource) => resource.id);

          const dto = {
            patientId: data.patient!.id!,
            caseId: caseId,
            note: data.note,
            title:
              translatedVisitTypes.find((type) => type.id === data.visitTypeId)
                ?.label || i18n.t(eventTypeTranslationKeys['Visit']),
            typeId: data.visitTypeId!,
            start,
            end,
            acceptConflict,
            resourceIds: resourceIds,
            inWaitingList: data.waitingList,
            priority: data.priority,
            attendingDoctorId: data.attendingDoctorId,
            statusId: data.statusId,
            number: data.number,
          };

          if (
            data.statusId === AppointmentStatusCodes.Canceled &&
            visitToEdit?.statusId != AppointmentStatusCodes.Canceled
          ) {
            // if existing visit is already canceled we shouldn't confirm cancellation again
            // (Bug 60994: Unneeded agreeing to cancel appointment)
            const isConfirmed = await modals.showConfirm({
              title: i18n.t('event.visit.confirm_cancel_visit_title'),
              text: i18n.t('event.visit.confirm_cancel_visit_text'),
              okButtonText: i18n.t('event.visit.confirm_cancel_visit_button'),
            });
            if (!isConfirmed) {
              return;
            }
          }

          let createdOrUpdatedVisit: VisitDto;
          try {
            if (
              data.patient &&
              !!data.priorityContactType &&
              data.priorityContactType !== data.patient?.priorityContactType
            ) {
              await updatePatientPriorityContact(
                data.patient!.id!,
                data.priorityContactType,
              );
            }
            createdOrUpdatedVisit = await createOrUpdateVisit(dto);
          } catch (e: any) {
            if (e.status === 409) {
              acceptConflict = (await showConflictDialog()) ?? false;
              if (acceptConflict) {
                dto.acceptConflict = acceptConflict;
                createdOrUpdatedVisit = await createOrUpdateVisit(dto);
              } else {
                return;
              }
            } else {
              throw e;
            }
          }

          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockQuery.getQueryKey(),
          });
          // open visit only if we create a new one (not editing)
          if (isOpenVisit && !visitId) {
            openVisit(createdOrUpdatedVisit, data.patient!);
          }

          if (data.statusId === AppointmentStatusCodes.Canceled) {
            await openCalendarTab(createdOrUpdatedVisit.start);
          }
        });
      } catch (err) {
        const errorDetails = handleSubmitFormError<VisitCreatingFormData>(
          err,
          setError,
        );
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    setError,
  );

  const selectPatient = useCallback(
    (patient: SearchInputPatientData | null) => {
      setValue(nameof('patient'), patient, validateAndDirty);
      if (patient?.priorityContactType) {
        setValue(
          nameof('priorityContactType'),
          patient.priorityContactType,
          validateAndDirty,
        );
      }
    },
    [setValue, subtenantsDict, setResourceSelected, setResourceGroups],
  );

  const addNewPatient = useCallback(
    async (selectInInput?: (patient: SearchInputPatientData) => void) => {
      const createdPatient = await globalForms.openPatientCreating(
        patient?.subtenantId,
        undefined,
        true,
      );
      if (createdPatient) {
        selectPatient(createdPatient);
        selectInInput?.(createdPatient);
      }
    },
    [selectPatient, globalForms, setValue, patient],
  );

  const modals = useModal();
  const calendarSettingsQuery =
    QueryFactory.CalendarSettingsQuery.useGetQuery();

  const openCalendarTab = useOpenCalendarTab(
    modals,
    undefined,
    tabs,
    calendarSettingsQuery.data?.defaultCalendarView,
  );
  const deleteVisit = useCallback(async () => {
    if (!visitId) {
      return;
    }
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('event.visit.confirm_delete_title'),
      text: i18n.t('event.visit.confirm_delete_text'),
      okButtonText: i18n.t('event.visit.confirm_delete_button'),
      okButtonColor: ButtonColor.SuperDanger,
    });
    if (isConfirmed) {
      await loadBlocking(
        formId,
        i18n.t('event.visit.loading_delete'),
        async () => {
          try {
            await tryCompleteForm(formType, formId, async () => {
              await QueryFactory.AppointmentEventsClient.deleteAppointment(
                visitId,
              );
            });

            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DataBlockQuery.getQueryKey(
                null,
                caseId,
                null,
                null,
              ),
            });

            await openCalendarTab(visitToEdit?.start!);
          } catch (err) {
            await modals.showError({ text: convertToErrorString(err) });
          }
        },
      ).catch((e) => console.error('deleteOrCancelVisit', e));
    }
  }, [formType, formId, tryCompleteForm, patient, caseId]);

  const expandForm = useCallback(() => expand(formId), [expand, formId]);

  // visits with past date cannot be added to waiting list
  const canAddToWaitingList = useMemo(() => {
    if (!startDateTime) {
      return true;
    }
    const date = new Date();
    return startDateTime >= date;
  }, [startDateTime]);

  const handleStatusChange = (newStatusValue: boolean) => {
    setStatus(newStatusValue);
  };

  const onChangeCase = useEventCallback(async (newCaseId: number) => {
    if (!!visitId) {
      const isConfirmed = await modals.showConfirm({
        title: i18n.t('event.visit.confirm_move_visit_title'),
        text: i18n.t('event.visit.confirm_move_visit_text'),
        okButtonText: i18n.t('event.visit.confirm_move_visit_button'),
        okButtonColor: ButtonColor.SuperDanger,
      });
      if (!isConfirmed) {
        return;
      }
    }
    setValue('caseId', newCaseId, validateAndDirty);
  });

  const formComponent = isExpanded ? (
    <VisitCreatingExpandedForm
      visitId={visitId ?? undefined}
      form={expandedForm}
      cases={cases}
      attendingDoctors={attendingDoctors}
      onAttendingDoctorsSearch={setDoctorsSearchQuery}
      isAttendingDoctorsLoading={false}
      alertPatientOptions={alertPatientOptions}
      addNewPatient={addNewPatient}
      addNewCase={addNewCase}
      onChangeCase={onChangeCase}
      onStatusChange={handleStatusChange}
      selectPatient={selectPatient}
      visitTypes={translatedVisitTypes}
      takenTimeSlots={takenTimeSlots}
      takenTimeSlotsForResources={takenTimeSlotsForResources}
      resourceSettings={resourceSettings}
      resourceGroups={resourceGroups}
      selectedResources={selectedResources}
      setResourceSelected={setResourceSelected}
      resourcesLoaded={resourcesLoaded}
      // if resources are passed (doctor may be considered as resource) visit is being created from resource view
      isWeekViewByDefault={isWeekView || !!assignedResources?.length}
      timeSlotTimeStepInMinutes={props.timeSlotTimeStepInMinutes}
      typesWithAnyAvailableResource={typesWithAnyAvailableResource}
      setTypesWithAnyAvailableResource={changeTypesWithAnyAvailableResource}
    />
  ) : (
    <VisitCreatingMiniForm
      form={miniForm}
      cases={cases}
      alertPatientOptions={alertPatientOptions}
      addNewPatient={addNewPatient}
      addNewCase={addNewCase}
      onChangeCase={onChangeCase}
      selectPatient={selectPatient}
      visitTypes={translatedVisitTypes}
      eventTypeSelection={eventTypeSelection}
      setResourceSelected={setResourceSelected}
    />
  );

  return (
    <form
      className={clsx(
        styles.form,
        isExpanded ? styles.expandedForm : styles.miniForm,
      )}
      onSubmit={handleSubmit(submitForm.handler)}
      data-test-id="visitEditingFormPopup"
    >
      <div className="globalFormContentRoot">
        <div className="globalFormContentBody">
          {props.visitId ? (
            <Loading loading={cases.length < 1}>{formComponent}</Loading>
          ) : (
            formComponent
          )}
        </div>
        <div className={styles.footer}>
          {!visitId && (
            <CheckBox
              checked={isOpenVisit}
              title={i18n.t('event.visit.open_patient_profile')}
              onChange={(event) =>
                ChangeOpenVisitSettings(event.currentTarget.checked)
              }
              className={styles.openPatientCheckbox}
              name="isOpenVisit"
              testId="open-visit-after-saving"
            />
          )}
          {visitId && claims.permissions.deleteVisits && (
            <div className={styles.buttonContainer}>
              <Button
                color={ButtonColor.Danger}
                className={styles.deleteButton}
                title={i18n.t('event.visit.delete_button')}
                onClick={() => deleteVisit()}
              />
            </div>
          )}
          {!isExpanded ? (
            <Button
              title={i18n.t('event.visit.more_options')}
              color={ButtonColor.Primary}
              onClick={expandForm}
              className={styles.moreOptionsButton}
            />
          ) : null}
          {isExpanded && (
            <div className={styles.waitingListContainer}>
              {canAddToWaitingList && (
                <>
                  <CheckBox
                    className={styles.waitingListCheckbox}
                    {...register('waitingList')}
                    title={i18n.t('event.visit.waiting_list')}
                  />
                  {inWaitingList && (
                    <div className={styles.priorityRadioContainer}>
                      <span className={styles.priorityLabel}>
                        {i18n.t('event.visit.fields.priority')}
                      </span>
                      <RadioButton
                        className={styles.leftRadioButton}
                        name={'priority-high'}
                        title={i18n.t('calendar.priority.high')}
                        checked={priority === WaitingListPriority.High}
                        onChange={() =>
                          setValue('priority', WaitingListPriority.High)
                        }
                      />
                      <RadioButton
                        className={styles.leftRadioButton}
                        name={'priority-medium'}
                        title={i18n.t('calendar.priority.medium')}
                        checked={priority === WaitingListPriority.Medium}
                        onChange={() =>
                          setValue('priority', WaitingListPriority.Medium)
                        }
                      />
                      <RadioButton
                        name={'priority-low'}
                        title={i18n.t('calendar.priority.low')}
                        checked={priority === WaitingListPriority.Low}
                        onChange={() =>
                          setValue('priority', WaitingListPriority.Low)
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <SubmitButton
            className={styles.submit}
            title={i18n.t('event.visit.save')}
            submitting={formState.isSubmitting}
            openCalendarTab={() => openCalendarTab}
            isCancel={status}
          />
        </div>
      </div>
    </form>
  );
};
function loadResourceGroups(
  resources: QueryFactory.ResourceDto[],
  visitId: number | null | undefined,
) {
  const groupedResources: ResourceGroups = {};
  resources.forEach((resource) => {
    let resourceList: ResourceToPick[] =
      groupedResources[ResourceType[resource.type]];
    if (!resourceList) {
      resourceList = [];
      groupedResources[ResourceType[resource.type]] = resourceList;
    }
    resourceList.push({
      id: resource.id,
      name: resource.name,
      isDeleted: resource.isDeleted,
      getTimeSlotsFromAppointments: (appointments) =>
        appointments
          .filter(
            (appointment) =>
              (!visitId || appointment.id !== visitId) &&
              appointment.resourceIds &&
              appointment.resourceIds.indexOf(resource.id) >= 0,
          )
          .map((appointment) => ({
            start: appointment.start,
            end: appointment.end,
          })),
    });
  });
  return groupedResources;
}
